import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Pizza } from "lucide-react";
import { BRL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function HalfPizzaDialog({ pizzas, open, onOpenChange }) {
  const { add } = useCart();
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [size, setSize] = useState("");

  const sizeNames = (pizzas[0]?.sizes || []).map((s) => s.name);
  const hasSizes = sizeNames.length > 0;

  const p1 = pizzas.find((p) => p.id === first);
  const p2 = pizzas.find((p) => p.id === second);

  const priceFor = (pz, sz) =>
    pz ? (pz.sizes?.find((s) => s.name === sz)?.price ?? pz.price) : 0;

  const ready = p1 && p2 && (!hasSizes || size);
  const price = ready ? Math.max(priceFor(p1, size), priceFor(p2, size)) : 0;

  const reset = () => {
    setFirst("");
    setSecond("");
    setSize("");
  };

  const addHalf = () => {
    if (!p1 || !p2) {
      toast.error("Escolha os dois sabores.");
      return;
    }
    if (p1.id === p2.id) {
      toast.error("Escolha dois sabores diferentes.");
      return;
    }
    if (hasSizes && !size) {
      toast.error("Escolha o tamanho.");
      return;
    }
    const names = [p1.name.replace(/^Pizza\s+/i, ""), p2.name.replace(/^Pizza\s+/i, "")];
    const sizeLabel = hasSizes ? ` (${size})` : "";
    const product = {
      id: `half:${[p1.id, p2.id].sort().join(":")}:${size}`,
      name: `Pizza Meio a Meio${sizeLabel}: ${names[0]} / ${names[1]}`,
      price,
    };
    add(product);
    toast.success("Pizza meio a meio adicionada!");
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent data-testid="half-pizza-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
            <Pizza className="w-6 h-6 text-primary" /> Pizza Meio a Meio
          </DialogTitle>
          <DialogDescription>
            Escolha 2 sabores{hasSizes ? " e o tamanho" : ""}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {hasSizes && (
            <div>
              <Label>Tamanho</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger data-testid="half-size">
                  <SelectValue placeholder="Escolha o tamanho" />
                </SelectTrigger>
                <SelectContent>
                  {sizeNames.map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>1º sabor</Label>
            <Select value={first} onValueChange={setFirst}>
              <SelectTrigger data-testid="half-flavor-1">
                <SelectValue placeholder="Escolha o primeiro sabor" />
              </SelectTrigger>
              <SelectContent>
                {pizzas.map((p) => (
                  <SelectItem key={p.id} value={p.id} disabled={p.id === second}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>2º sabor</Label>
            <Select value={second} onValueChange={setSecond}>
              <SelectTrigger data-testid="half-flavor-2">
                <SelectValue placeholder="Escolha o segundo sabor" />
              </SelectTrigger>
              <SelectContent>
                {pizzas.map((p) => (
                  <SelectItem key={p.id} value={p.id} disabled={p.id === first}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between bg-muted/50 rounded-xl p-3">
            <span className="text-muted-foreground text-sm">Valor da pizza</span>
            <span className="font-display font-black text-xl text-primary" data-testid="half-price">
              {price ? BRL(price) : "—"}
            </span>
          </div>

          <button
            onClick={addHalf}
            data-testid="add-half-pizza-btn"
            className="w-full rounded-full bg-primary text-primary-foreground py-3 font-semibold hover:bg-primary/90 transition-colors"
          >
            Adicionar ao pedido
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
